import React, { useEffect, useState } from "react";
import {
  FieldType,
  OptionType,
  SubOptionType,
} from "../../../services/helpers/types";
import { useDispatch } from "react-redux";
import {
  addBaseOptionSelect,
  createTooltip,
  getValueObjectFromValue,
  handleAddNotSelectedSubOption,
  handleAlreadySelectedSubOption,
  mapSelectedValuesHelper,
  removeBaseOptionSelect,
  transformValueObject,
  transformValueObjectName,
  ValueObjectType,
} from "./helpers";
import CascaderSubOptions from "../../molecules/CascaderSubOptions/CascaderSubOptions";
import CascaderOptions from "../../molecules/CascaderOptions/CascaderOptions";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

interface CascaderFieldProps {
  field?: FieldType;
  value?: string;
  onChange?: (value: string) => void;
}

const CascaderField = (props: CascaderFieldProps) => {
  const { field, value = "", onChange } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [currentValue, setCurrentValue] = useState<string[]>([value]);
  const [currentValueObject, setCurrentValueObject] = useState<ValueObjectType>(
    {},
  );

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [selectOpen, setSelectOpen] = useState(false);

  const [options, setOptions] = useState<OptionType[]>([]);
  const [currentOption, setCurrentOption] = useState<OptionType>();

  //EXPORT

  useEffect(() => {
    if (value && value !== "") {
      const currentSelected = value.split(";");
      const valueObject = getValueObjectFromValue(currentSelected);
      const transformedCurrentSelectedRaw = transformValueObjectName(
        valueObject,
        options,
        currentOption,
      );

      const transformedCurrentSelected =
        transformedCurrentSelectedRaw.split(";");

      setCurrentValue(transformedCurrentSelected);
      const transformedValueObject = getValueObjectFromValue(
        transformedCurrentSelected,
      );

      setCurrentValueObject(transformedValueObject);
    } else {
      setCurrentValue([]);
      setCurrentValueObject({});
    }
  }, [currentOption, options, value]);

  useEffect(() => {
    if (field?.options) {
      setOptions(field.options);
    }
  }, [dispatch, field, value]);

  const handleSubSelectOpen = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    option: OptionType,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (option.subOptions !== undefined) {
      setCurrentOption(option);
      setAnchorEl(event.currentTarget);
      setSelectOpen(true);
    } else {
      setAnchorEl(null);
    }
  };

  const handleSubMenuFocusCapture = (
    event?: React.FocusEvent<HTMLDivElement>,
  ) => {
    if (event !== undefined && event.relatedTarget === null) {
      event.preventDefault();
      event.stopPropagation();
      setAnchorEl(null);
      setSelectOpen(false);
    }
  };

  const mapSelectedValues = (
    newValueObject: ValueObjectType,
    currOption?: OptionType,
  ) => {
    setCurrentValueObject(newValueObject);
    const transformedtextValue = transformValueObject(
      newValueObject,
      options,
      currOption ?? currentOption,
    );

    if (onChange) {
      onChange(transformedtextValue);
    }
    const { result } = mapSelectedValuesHelper(newValueObject);

    setCurrentValue(result);
  };

  const handleSubOptionSelect = (subOption: SubOptionType) => {
    const currentValueObjectKeys = Object.keys(currentValueObject);

    if (
      currentOption !== undefined &&
      currentValueObjectKeys.includes(currentOption.name)
    ) {
      handleAlreadySelectedSubOption(
        currentValueObject,
        currentOption,
        subOption,
        mapSelectedValues,
      );
    } else {
      handleAddNotSelectedSubOption(
        currentValueObject,
        subOption,
        mapSelectedValues,
        currentOption,
      );
    }
  };

  const handleBaseOptionSelect = (option: OptionType) => {
    const currentlyClickedOption = currentValueObject[option.name];
    if (currentlyClickedOption === undefined) {
      addBaseOptionSelect(
        option,
        currentValueObject,
        option,
        mapSelectedValues,
      );
    } else {
      removeBaseOptionSelect(option, currentValueObject, mapSelectedValues);
    }
  };

  return (
    <Tooltip
      title={createTooltip(currentValue, t)}
      placement="left-start"
      open={selectOpen}
    >
      <div className="dui-cascader-field-wrapper">
        <CascaderSubOptions
          anchorEl={anchorEl}
          currentOption={currentOption}
          currentValueObject={currentValueObject}
          handleSubMenuFocusCapture={handleSubMenuFocusCapture}
          handleSubOptionSelect={handleSubOptionSelect}
        />
        <CascaderOptions
          currentValue={currentValue}
          currentValueObject={currentValueObject}
          onBaseOptionSelect={handleBaseOptionSelect}
          onSelectOpen={(isOpen) => {
            setSelectOpen(isOpen);
          }}
          onSubSelectOpen={handleSubSelectOpen}
          options={options}
          selectOpen={selectOpen}
        />
      </div>
    </Tooltip>
  );
};

export default CascaderField;
